import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { type FormEvent, Fragment, useMemo, useState } from "react";
import { useConfirm } from "../features/canvas-kit/ui/confirm-dialog.jsx";
import { useSync } from "../providers/sync-provider";
import {
  deleteEvent,
  deleteSeries,
  EVENT_TYPES,
  type EventRow,
  type EventType,
  listEventsInRange,
  listUpcomingEvents,
  updateSeries,
  upsertEvent,
} from "../repos/events";

/* ────────────────────────── design tokens ────────────────────────── */

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;
const MONTHS = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
] as const;

const TYPE_TONE: Record<
  EventType,
  { label: string; bg: string; fg: string; dot: string }
> = {
  training: {
    label: "Training",
    bg: "rgba(151,252,0,0.16)",
    fg: "#d6ff8c",
    dot: "rgba(151,252,0,0.85)",
  },
  match: {
    label: "Spiel",
    bg: "rgba(255,176,46,0.18)",
    fg: "#ffd28a",
    dot: "rgba(255,176,46,0.90)",
  },
  meeting: {
    label: "Meeting",
    bg: "rgba(120,170,255,0.18)",
    fg: "#bcd5ff",
    dot: "rgba(120,170,255,0.90)",
  },
  medical: {
    label: "Medizinisch",
    bg: "rgba(255,121,143,0.18)",
    fg: "#ffc1cc",
    dot: "rgba(255,121,143,0.90)",
  },
  travel: {
    label: "Reise",
    bg: "rgba(189,156,255,0.18)",
    fg: "#d8c6ff",
    dot: "rgba(189,156,255,0.90)",
  },
  other: {
    label: "Sonstiges",
    bg: "rgba(190,200,210,0.16)",
    fg: "#d5dde6",
    dot: "rgba(190,200,210,0.85)",
  },
};

/* ─────────────────────────── page ─────────────────────────── */

/**
 * Kalender — month-grid calendar backed by the local-first `calendar_event`
 * table. Phase 1: no external provider integrations (no Google/iCal); every
 * change writes to SQLite, enqueues an outbox op, and the sync engine pushes
 * to the server on the next tick — so the calendar works fully offline and
 * shows up on the user's other devices automatically.
 */
export function CalendarPage() {
  const { db, orgId, userId, status } = useSync();
  const qc = useQueryClient();

  const today = useMemo(() => startOfDay(new Date()), []);
  const [cursor, setCursor] = useState<Date>(() => startOfMonth(today));
  const [picked, setPicked] = useState<Date | null>(null);
  const [editing, setEditing] = useState<EventRow | null>(null);
  const [creating, setCreating] = useState<Date | null>(null);

  const ready = Boolean(db && orgId && userId);

  // 6-week grid window (always 42 cells).
  const gridStart = useMemo(() => startOfWeek(startOfMonth(cursor)), [cursor]);
  const gridEnd = useMemo(() => addDays(gridStart, 42), [gridStart]);

  const monthEvents = useQuery<EventRow[]>({
    queryKey: [
      "events",
      orgId,
      userId,
      gridStart.toISOString(),
      gridEnd.toISOString(),
    ],
    enabled: ready,
    queryFn: () =>
      listEventsInRange(
        db!,
        orgId!,
        userId!,
        gridStart.toISOString(),
        gridEnd.toISOString(),
      ),
  });

  const upcoming = useQuery<EventRow[]>({
    queryKey: ["events", "upcoming", orgId, userId],
    enabled: ready,
    queryFn: () =>
      listUpcomingEvents(db!, orgId!, userId!, today.toISOString(), 6),
  });

  const save = useMutation({
    mutationFn: async (payload: {
      id?: string;
      title: string;
      description: string | null;
      type: EventType;
      startsAt: string;
      endsAt: string | null;
      location: string | null;
      seriesId?: string | null;
      allDay?: boolean;
    }) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await upsertEvent(db!, {
        ...payload,
        orgId: orgId!,
        ownerKind: "coach",
        ownerId: userId!,
      });
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["events"], exact: false }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await deleteEvent(db!, { id, orgId: orgId! });
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["events"], exact: false }),
  });

  const removeSeries = useMutation({
    mutationFn: async (args: { seriesId: string; fromIso: string | null }) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await deleteSeries(db!, {
        seriesId: args.seriesId,
        orgId: orgId!,
        fromIso: args.fromIso,
      });
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["events"], exact: false }),
  });

  const editSeries = useMutation({
    mutationFn: async (args: {
      seriesId: string;
      fromIso: string | null;
      changes: Parameters<typeof updateSeries>[1]["changes"];
    }) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await updateSeries(db!, {
        seriesId: args.seriesId,
        orgId: orgId!,
        fromIso: args.fromIso,
        changes: args.changes,
      });
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["events"], exact: false }),
  });

  const eventsByDay = useMemo(() => {
    const m = new Map<string, EventRow[]>();
    for (const ev of monthEvents.data ?? []) {
      const key = isoDateKey(new Date(ev.starts_at));
      const arr = m.get(key);
      if (arr) arr.push(ev);
      else m.set(key, [ev]);
    }
    return m;
  }, [monthEvents.data]);

  const days = useMemo(() => {
    const arr: Date[] = [];
    for (let i = 0; i < 42; i++) arr.push(addDays(gridStart, i));
    return arr;
  }, [gridStart]);

  const monthLabel = `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const eventCount = monthEvents.data?.length ?? 0;

  return (
    <div className="flex flex-col gap-4">
      {/* ── toolbar ─────────────────────────────────────── */}
      {/* Mobile: vertikales Stack (Titel → Nav → CTA). Ab sm wieder eine
          Zeile mit flex-wrap, damit auf Tablet/Desktop nichts umbricht. */}
      <section className="glass-floating flex flex-col gap-3 rounded-3xl px-5 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:px-7 sm:py-5">
        <div className="min-w-0 sm:flex-1">
          <h1
            className="home-display text-[20px] font-bold capitalize"
            style={{ color: "#f4f5f3" }}
          >
            {monthLabel}
          </h1>
          <p
            className="text-[12px]"
            style={{ color: "var(--color-on-surface-variant)" }}
          >
            {ready
              ? `${eventCount} Termin${eventCount === 1 ? "" : "e"} im Sichtfenster${status.kind === "idle" ? "" : ` · ${status.kind}`}`
              : "Lade lokale Datenbank…"}
          </p>
        </div>

        <div className="flex items-center justify-between gap-1 sm:justify-start">
          <button
            type="button"
            onClick={() => setCursor((c) => addMonths(c, -1))}
            className="grid size-9 place-items-center rounded-xl transition-colors hover:bg-white/5"
            style={{
              border: "1px solid rgba(255,255,255,0.10)",
              color: "var(--color-on-surface-variant)",
            }}
            aria-label="Vorheriger Monat"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setCursor(startOfMonth(today));
              setPicked(today);
            }}
            className="rounded-xl px-3 py-2 text-[12px] font-semibold uppercase tracking-widest transition-colors hover:bg-white/5"
            style={{
              border: "1px solid rgba(255,255,255,0.10)",
              color: "var(--color-on-surface-variant)",
            }}
          >
            Heute
          </button>
          <button
            type="button"
            onClick={() => setCursor((c) => addMonths(c, 1))}
            className="grid size-9 place-items-center rounded-xl transition-colors hover:bg-white/5"
            style={{
              border: "1px solid rgba(255,255,255,0.10)",
              color: "var(--color-on-surface-variant)",
            }}
            aria-label="Nächster Monat"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>

        <button
          type="button"
          onClick={() => {
            if (!ready) return;
            setCreating(picked ?? today);
          }}
          disabled={!ready}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold transition-colors disabled:opacity-60 sm:w-auto"
          style={{
            background: "var(--color-pitch-lime)",
            color: "#1d3700",
            boxShadow: "0 6px 16px -4px rgba(151,252,0,0.45)",
          }}
        >
          <Plus className="size-4" />
          Termin
        </button>
      </section>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        {/* ── month grid ─────────────────────────── */}
        {/* 8-Spalten-Grid: erste Spalte trägt die Kalenderwoche (KW), die
            restlichen 7 die Wochentage. Header und Datenrow nutzen dasselbe
            Template damit alles bündig bleibt. */}
        <section className="glass-floating overflow-hidden rounded-3xl">
          <div
            className="grid grid-cols-[40px_repeat(7,minmax(0,1fr))] border-b px-2 pt-2 sm:grid-cols-[48px_repeat(7,minmax(0,1fr))]"
            style={{ borderColor: "rgba(255,255,255,0.06)" }}
          >
            <div
              className="px-1 pb-2 text-center text-[11px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: "var(--color-on-surface-variant)" }}
            >
              KW
            </div>
            {WEEKDAYS.map((d, i) => {
              const weekend = i >= 5;
              return (
                <div
                  key={d}
                  className="px-2 pb-2 text-center text-[12px] font-bold uppercase tracking-[0.16em]"
                  style={{ color: weekend ? "#ffc185" : "#f4f5f3" }}
                >
                  {d}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-[40px_repeat(7,minmax(0,1fr))] sm:grid-cols-[48px_repeat(7,minmax(0,1fr))]">
            {days.map((day, idx) => {
              const inMonth = day.getMonth() === cursor.getMonth();
              const isToday = isSameDay(day, today);
              const isPicked = picked != null && isSameDay(day, picked);
              const evs = eventsByDay.get(isoDateKey(day)) ?? [];
              const visible = evs.slice(0, 3);
              const overflow = evs.length - visible.length;
              const dow = day.getDay(); // 0=So..6=Sa
              const isWeekend = dow === 0 || dow === 6;
              const isFirstOfRow = idx % 7 === 0;

              return (
                <Fragment key={idx}>
                  {isFirstOfRow && (
                    <div
                      className="flex items-start justify-center border-b border-r pt-2 text-[11px] font-semibold tabular-nums"
                      style={{
                        borderColor: "rgba(255,255,255,0.05)",
                        color: "var(--color-on-surface-variant)",
                        background: "rgba(255,255,255,0.015)",
                      }}
                    >
                      {isoWeek(day)}
                    </div>
                  )}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setPicked(day);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setPicked(day);
                      }
                    }}
                    onDoubleClick={() => ready && setCreating(day)}
                    className="group relative flex min-h-27.5 cursor-pointer flex-col gap-1.5 border-b border-r px-2 pb-2 pt-2 text-left transition-colors hover:bg-white/3 focus-visible:outline-2 focus-visible:-outline-offset-0.5 focus-visible:outline-(--color-pitch-lime)"
                    style={{
                      borderColor: "rgba(255,255,255,0.05)",
                      background: isPicked
                        ? "rgba(151,252,0,0.06)"
                        : isWeekend
                          ? "rgba(255,193,133,0.035)"
                          : "transparent",
                      opacity: inMonth ? 1 : 0.4,
                    }}
                  >
                    <span
                      className="inline-flex size-7 items-center justify-center rounded-full text-[12px] font-semibold tabular-nums"
                      style={
                        isToday
                          ? {
                              background: "var(--color-pitch-lime)",
                              color: "#1d3700",
                              boxShadow: "0 0 0 3px rgba(151,252,0,0.18)",
                            }
                          : {
                              color: inMonth
                                ? "#f4f5f3"
                                : "var(--color-on-surface-variant)",
                            }
                      }
                    >
                      {day.getDate()}
                    </span>

                    <div className="flex min-h-0 flex-1 flex-col gap-1">
                      {visible.map((ev) => {
                        const tone =
                          TYPE_TONE[(ev.type as EventType) ?? "other"];
                        const isAllDay = Boolean(ev.all_day);
                        return (
                          <button
                            key={ev.id}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditing(ev);
                            }}
                            className="truncate rounded-md px-1.5 py-0.5 text-left text-[10.5px] font-medium"
                            style={{ background: tone.bg, color: tone.fg }}
                            title={
                              isAllDay
                                ? `Ganztägig · ${ev.title}`
                                : `${formatTime(ev.starts_at)} · ${ev.title}`
                            }
                          >
                            <span className="opacity-70">
                              {isAllDay
                                ? "Ganztägig"
                                : formatTime(ev.starts_at)}
                            </span>{" "}
                            {ev.title}
                          </button>
                        );
                      })}
                      {overflow > 0 && (
                        <span
                          className="px-1.5 text-[10px]"
                          style={{ color: "var(--color-on-surface-variant)" }}
                        >
                          +{overflow} weitere
                        </span>
                      )}
                    </div>

                    {ready && (
                      // Real button (not just a hover affordance) — single
                      // click on the day cell only *picks* the day; clicking
                      // the + opens the create dialog directly.
                      // Nested-button markup is already used for the event
                      // chips above and browsers accept it.
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCreating(day);
                        }}
                        aria-label={`Termin am ${day.getDate()}. ${MONTHS[day.getMonth()]} hinzufügen`}
                        className="absolute right-1.5 top-1.5 grid size-5 place-items-center rounded-full opacity-0 transition-opacity duration-fast group-hover:opacity-100 hover:scale-110 focus-visible:opacity-100"
                        style={{
                          background: "rgba(10,12,8,0.7)",
                          color: "var(--color-pitch-lime)",
                          backdropFilter: "blur(8px)",
                        }}
                      >
                        <Plus className="size-3" />
                      </button>
                    )}
                  </div>
                </Fragment>
              );
            })}
          </div>
        </section>

        {/* ── upcoming sidebar ─────────────────────── */}
        <aside className="flex flex-col gap-4">
          <section className="glass-floating rounded-3xl p-5">
            <header className="mb-3 flex items-center gap-2">
              <CalendarDays
                className="size-4"
                style={{ color: "var(--color-pitch-lime)" }}
              />
              <h2
                className="home-display text-[14px] font-bold uppercase tracking-[0.12em]"
                style={{ color: "#f4f5f3" }}
              >
                Anstehend
              </h2>
            </header>

            {!ready ? (
              <p
                className="text-[12px]"
                style={{ color: "var(--color-on-surface-variant)" }}
              >
                Lade…
              </p>
            ) : (upcoming.data ?? []).length === 0 ? (
              <p
                className="text-[12px]"
                style={{ color: "var(--color-on-surface-variant)" }}
              >
                Keine kommenden Termine. Klick auf einen Tag oder „+ Termin“.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {(upcoming.data ?? []).map((ev) => (
                  <UpcomingRow
                    key={ev.id}
                    ev={ev}
                    onOpen={() => setEditing(ev)}
                  />
                ))}
              </ul>
            )}
          </section>

          <section className="glass-floating rounded-3xl p-5">
            <h3
              className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.16em]"
              style={{ color: "var(--color-on-surface-variant)" }}
            >
              Legende
            </h3>
            {/* Kompaktere Legende: 2 Spalten, etwas größere Schrift,
                engerer Zeilenabstand und größere Farbpunkte für besseren
                Scan-Effekt. */}
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {EVENT_TYPES.map((t) => (
                <li
                  key={t}
                  className="flex items-center gap-2 text-[13px] leading-tight"
                  style={{ color: "#f4f5f3" }}
                >
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ background: TYPE_TONE[t].dot }}
                    aria-hidden
                  />
                  <span className="truncate">{TYPE_TONE[t].label}</span>
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>

      {(creating || editing) && (
        <EventDialog
          initial={editing}
          defaultDate={creating}
          busy={
            save.isPending ||
            remove.isPending ||
            removeSeries.isPending ||
            editSeries.isPending
          }
          onClose={() => {
            setCreating(null);
            setEditing(null);
          }}
          onSave={async (payloads) => {
            // `payloads` is always at least one item; for a recurring
            // creation the dialog expands the series client-side and we
            // persist each instance as an independent row \u2014 every row
            // carries the same `seriesId` so the user can later edit or
            // delete \u201ediesen und folgende\u201c / \u201ealle Termine der Serie\u201c.
            for (const p of payloads) {
              await save.mutateAsync(p);
            }
            setCreating(null);
            setEditing(null);
          }}
          onUpdateSeries={async (args) => {
            await editSeries.mutateAsync(args);
            setCreating(null);
            setEditing(null);
          }}
          onDelete={(id) =>
            remove.mutateAsync(id).then(() => {
              setEditing(null);
            })
          }
          onDeleteSeries={(args) =>
            removeSeries.mutateAsync(args).then(() => {
              setEditing(null);
            })
          }
        />
      )}
    </div>
  );
}

/* ─────────────────────────── upcoming row ─────────────────────────── */

function UpcomingRow({ ev, onOpen }: { ev: EventRow; onOpen: () => void }) {
  const tone = TYPE_TONE[(ev.type as EventType) ?? "other"];
  const date = new Date(ev.starts_at);
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="group flex w-full items-stretch gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors hover:bg-white/4"
      >
        <div
          className="flex w-12 shrink-0 flex-col items-center justify-center rounded-xl py-1"
          style={{
            background: "rgba(10,12,8,0.45)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <span
            className="text-[9px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: "var(--color-on-surface-variant)" }}
          >
            {MONTHS[date.getMonth()]!.slice(0, 3)}
          </span>
          <span
            className="text-[18px] font-bold leading-none tabular-nums"
            style={{ color: "#f4f5f3" }}
          >
            {date.getDate()}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-[13px] font-semibold"
            style={{ color: "#f4f5f3" }}
          >
            {ev.title}
          </p>
          <p
            className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]"
            style={{ color: "var(--color-on-surface-variant)" }}
          >
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3" />{" "}
              {ev.all_day
                ? "Ganztägig"
                : `${formatTime(ev.starts_at)}${ev.ends_at ? ` – ${formatTime(ev.ends_at)}` : ""}`}
            </span>
            {ev.location && (
              <span className="inline-flex items-center gap-1 truncate">
                <MapPin className="size-3" /> {ev.location}
              </span>
            )}
          </p>
          <span
            className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em]"
            style={{ background: tone.bg, color: tone.fg }}
          >
            <span
              className="size-1.5 rounded-full"
              style={{ background: tone.dot }}
              aria-hidden
            />
            {tone.label}
          </span>
        </div>
      </button>
    </li>
  );
}

/* ─────────────────────────── event dialog ─────────────────────────── */

type RepeatKind = "none" | "weekly" | "biweekly" | "monthly";

const REPEAT_OPTIONS: { value: RepeatKind; label: string }[] = [
  { value: "none", label: "Keine" },
  { value: "weekly", label: "Wöchentlich" },
  { value: "biweekly", label: "14-tägig" },
  { value: "monthly", label: "Monatlich" },
];

const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;

/** How the user wants an edit/delete to apply to a series. */
type SeriesScope = "single" | "future" | "all";

interface SavePayload {
  id?: string;
  title: string;
  description: string | null;
  type: EventType;
  startsAt: string;
  endsAt: string | null;
  location: string | null;
  seriesId: string | null;
  allDay: boolean;
}

function EventDialog({
  initial,
  defaultDate,
  busy,
  onClose,
  onSave,
  onUpdateSeries,
  onDelete,
  onDeleteSeries,
}: {
  initial: EventRow | null;
  defaultDate: Date | null;
  busy: boolean;
  onClose: () => void;
  onSave: (payloads: SavePayload[]) => Promise<unknown>;
  onUpdateSeries: (args: {
    seriesId: string;
    fromIso: string | null;
    changes: {
      title?: string;
      description?: string | null;
      type?: EventType;
      location?: string | null;
      allDay?: boolean;
      startTime?: string | null;
      endTime?: string | null;
    };
  }) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  onDeleteSeries: (args: {
    seriesId: string;
    fromIso: string | null;
  }) => Promise<unknown>;
}) {
  const isSeriesMember = Boolean(initial?.series_id);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const seed = useMemo(() => {
    if (initial) {
      const start = new Date(initial.starts_at);
      const end = initial.ends_at ? new Date(initial.ends_at) : null;
      return {
        title: initial.title,
        description: initial.description ?? "",
        type: (initial.type as EventType) ?? "training",
        date: isoDateKey(start),
        startTime: formatTime(initial.starts_at),
        endTime: end ? formatTime(end.toISOString()) : "",
        location: initial.location ?? "",
        allDay: Boolean(initial.all_day),
        startsAtIso: initial.starts_at,
      };
    }
    const base = defaultDate ?? new Date();
    const now = new Date();
    const sameDay = isSameDay(base, now);
    const hour = sameDay ? Math.max(now.getHours() + 1, 9) : 18;
    return {
      title: "",
      description: "",
      type: "training" as EventType,
      date: isoDateKey(base),
      startTime: `${pad2(hour)}:00`,
      endTime: `${pad2(Math.min(hour + 1, 23))}:30`,
      location: "",
      allDay: false,
      startsAtIso: null as string | null,
    };
  }, [initial, defaultDate]);

  const [title, setTitle] = useState(seed.title);
  const [description, setDescription] = useState(seed.description);
  const [type, setType] = useState<EventType>(seed.type);
  const [date, setDate] = useState(seed.date);
  const [startTime, setStartTime] = useState(seed.startTime);
  const [endTime, setEndTime] = useState(seed.endTime);
  const [location, setLocation] = useState(seed.location);
  const [allDay, setAllDay] = useState(seed.allDay);

  // Recurrence is only offered on creation. Editing a series shows
  // an edit-scope picker instead (single / future / all) below.
  const [repeat, setRepeat] = useState<RepeatKind>("none");
  const [repeatCount, setRepeatCount] = useState(8);
  // Selected weekdays for `weekly` recurrence (0=Mo..6=So). Default to
  // the weekday of the start date.
  const initialWeekday = useMemo(() => mondayWeekday(seed.date), [seed.date]);
  const [weekdays, setWeekdays] = useState<number[]>([initialWeekday]);

  // Scope picker for editing/deleting a row that belongs to a series.
  const [scope, setScope] = useState<SeriesScope>("single");

  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError("Titel ist erforderlich.");
      return;
    }

    // All-day: pin to 00:00 → next-day 00:00 in local TZ. Time inputs
    // are hidden in that case so we ignore whatever they hold.
    const effectiveStart = allDay ? "00:00" : startTime;
    const effectiveEnd = allDay ? "00:00" : endTime;

    const startsAt = combineDateTime(date, effectiveStart);
    if (!startsAt) {
      setError("Ungültiges Startdatum.");
      return;
    }

    let endsAt: string | null = null;
    if (allDay) {
      const startDt = new Date(startsAt);
      const next = new Date(startDt);
      next.setDate(next.getDate() + 1);
      endsAt = next.toISOString();
    } else if (effectiveEnd.trim()) {
      endsAt = combineDateTime(date, effectiveEnd);
      if (!endsAt) {
        setError("Ungültiges Enddatum.");
        return;
      }
      if (Date.parse(endsAt) <= Date.parse(startsAt)) {
        setError("Ende muss nach dem Start liegen.");
        return;
      }
    }

    try {
      const base = {
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        type,
        location: location.trim() ? location.trim() : null,
        allDay,
      };

      // ── Series edit (scope: future / all) ─────────────────────────
      if (initial && isSeriesMember && scope !== "single") {
        await onUpdateSeries({
          seriesId: initial.series_id!,
          fromIso: scope === "future" ? initial.starts_at : null,
          changes: {
            title: base.title,
            description: base.description,
            type: base.type,
            location: base.location,
            allDay: base.allDay,
            startTime: allDay ? null : startTime,
            endTime: allDay ? null : endTime || null,
          },
        });
        return;
      }

      // ── Single-row save (create new, or edit one instance) ────────
      const payloads: SavePayload[] = [];

      if (initial || repeat === "none") {
        payloads.push({
          ...base,
          id: initial?.id,
          startsAt,
          endsAt,
          seriesId: initial?.series_id ?? null,
        });
      } else {
        // Expand the series client-side. Every occurrence shares the
        // same `seriesId` so later edits can target single / future /
        // all instances.
        const count = Math.max(1, Math.min(repeatCount, 52));
        const seriesId = crypto.randomUUID();
        const startBase = new Date(startsAt);
        const endBase = endsAt ? new Date(endsAt) : null;
        const durationMs =
          endBase != null ? endBase.getTime() - startBase.getTime() : null;

        const occurrences: Date[] = [];
        if (repeat === "weekly") {
          // Multiple weekdays per week × count weeks (count weeks
          // starting at the week of the seed date, including the seed
          // day's weekday only if it is in `weekdays`).
          const picks = weekdays.length > 0 ? weekdays : [initialWeekday];
          const sortedPicks = [...picks].sort((a, b) => a - b);
          const seedWeekday = mondayWeekday(date);
          const seedWeekStart = new Date(startBase);
          seedWeekStart.setDate(seedWeekStart.getDate() - seedWeekday);
          for (let w = 0; w < count; w++) {
            for (const dow of sortedPicks) {
              const d = new Date(seedWeekStart);
              d.setDate(d.getDate() + w * 7 + dow);
              d.setHours(startBase.getHours(), startBase.getMinutes(), 0, 0);
              if (d.getTime() < startBase.getTime()) continue; // skip past
              occurrences.push(d);
            }
          }
          if (occurrences.length === 0) occurrences.push(new Date(startBase));
        } else if (repeat === "biweekly") {
          for (let i = 0; i < count; i++) {
            const d = new Date(startBase);
            d.setDate(d.getDate() + i * 14);
            occurrences.push(d);
          }
        } else {
          // monthly
          for (let i = 0; i < count; i++) {
            const d = new Date(startBase);
            d.setMonth(d.getMonth() + i);
            occurrences.push(d);
          }
        }

        for (const s of occurrences) {
          const e =
            durationMs != null ? new Date(s.getTime() + durationMs) : null;
          payloads.push({
            ...base,
            startsAt: s.toISOString(),
            endsAt: e ? e.toISOString() : null,
            seriesId,
          });
        }
      }

      await onSave(payloads);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    }
  }

  async function handleDelete() {
    if (!initial) return;
    if (isSeriesMember && scope !== "single") {
      const message =
        scope === "future"
          ? `Diesen und alle folgenden Termine der Serie „${initial.title}“ wirklich löschen?`
          : `Alle Termine der Serie „${initial.title}“ wirklich löschen?`;
      const ok = await confirm({
        title: "Serientermine löschen?",
        message,
        confirmLabel: "Löschen",
      });
      if (!ok) return;
      await onDeleteSeries({
        seriesId: initial.series_id!,
        fromIso: scope === "future" ? initial.starts_at : null,
      });
      return;
    }
    const ok = await confirm({
      title: "Termin löschen?",
      message: `„${initial.title}“ wird unwiderruflich gelöscht.`,
      confirmLabel: "Löschen",
    });
    if (!ok) return;
    await onDelete(initial.id);
  }

  function toggleWeekday(idx: number) {
    setWeekdays((prev) =>
      prev.includes(idx) ? prev.filter((x) => x !== idx) : [...prev, idx],
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 py-8"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="presentation"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="glass-floating relative flex max-h-[92vh] w-full max-w-lg flex-col gap-4 overflow-y-auto rounded-3xl p-6"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3">
          <div>
            <h2
              className="home-display text-[18px] font-bold"
              style={{ color: "#f4f5f3" }}
            >
              {initial ? "Termin bearbeiten" : "Neuer Termin"}
            </h2>
            <p
              className="mt-0.5 text-[11px]"
              style={{ color: "var(--color-on-surface-variant)" }}
            >
              {isSeriesMember
                ? "Teil einer Terminserie."
                : "Lokal gespeichert, automatisch auf deine anderen Geräte synchronisiert."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-8 place-items-center rounded-xl transition-colors hover:bg-white/5"
            style={{
              border: "1px solid rgba(255,255,255,0.10)",
              color: "var(--color-on-surface-variant)",
            }}
            aria-label="Schließen"
          >
            <X className="size-4" />
          </button>
        </header>

        <Field label="Titel">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z. B. Abschlusstraining"
            autoFocus
            required
            className="h-10 w-full rounded-xl px-3 text-[13px] outline-none transition-colors"
            style={inputStyle}
          />
        </Field>

        <Field label="Typ">
          <div className="flex flex-wrap gap-1.5">
            {EVENT_TYPES.map((t) => {
              const tone = TYPE_TONE[t];
              const active = type === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all"
                  style={{
                    background: active ? tone.bg : "rgba(255,255,255,0.04)",
                    color: active ? tone.fg : "var(--color-on-surface-variant)",
                    border: active
                      ? `1px solid ${tone.dot}`
                      : "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <span
                    className="size-1.5 rounded-full"
                    style={{ background: tone.dot }}
                    aria-hidden
                  />
                  {tone.label}
                </button>
              );
            })}
          </div>
        </Field>

        {/* All-day toggle */}
        <label
          className="flex items-center gap-3 rounded-xl px-3 py-2.5"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <input
            type="checkbox"
            checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
            className="size-4 accent-(--color-pitch-lime)"
          />
          <span
            className="flex-1 text-[12.5px] font-medium"
            style={{ color: "#f4f5f3" }}
          >
            Ganztägig
          </span>
          <span
            className="text-[10.5px]"
            style={{ color: "var(--color-on-surface-variant)" }}
          >
            keine Uhrzeit
          </span>
        </label>

        <div
          className={
            allDay
              ? "grid grid-cols-1 gap-3"
              : "grid grid-cols-[1fr_auto_auto] gap-3"
          }
        >
          <Field label="Datum">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="h-10 w-full rounded-xl px-3 text-[13px] outline-none"
              style={inputStyle}
            />
          </Field>
          {!allDay && (
            <>
              <Field label="Von">
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                  className="h-10 w-27.5 rounded-xl px-3 text-[13px] outline-none"
                  style={inputStyle}
                />
              </Field>
              <Field label="Bis">
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="h-10 w-27.5 rounded-xl px-3 text-[13px] outline-none"
                  style={inputStyle}
                />
              </Field>
            </>
          )}
        </div>

        <Field label="Ort (optional)">
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="z. B. Trainingsplatz 2"
            className="h-10 w-full rounded-xl px-3 text-[13px] outline-none"
            style={inputStyle}
          />
        </Field>

        {/* ── Recurrence picker (only on creation) ─────────────────── */}
        {!initial && (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-[1fr_auto] gap-3">
              <Field label="Wiederholen">
                <select
                  value={repeat}
                  onChange={(e) => setRepeat(e.target.value as RepeatKind)}
                  className="h-10 w-full rounded-xl px-3 text-[13px] outline-none"
                  style={inputStyle}
                >
                  {REPEAT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={repeat === "weekly" ? "Wochen" : "Anzahl"}>
                <input
                  type="number"
                  min={1}
                  max={52}
                  value={repeatCount}
                  disabled={repeat === "none"}
                  onChange={(e) =>
                    setRepeatCount(
                      Math.max(1, Math.min(52, Number(e.target.value) || 1)),
                    )
                  }
                  className="h-10 w-22.5 rounded-xl px-3 text-[13px] outline-none disabled:opacity-50"
                  style={inputStyle}
                />
              </Field>
            </div>

            {repeat === "weekly" && (
              <Field label="Wochentage">
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAY_LABELS.map((label, idx) => {
                    const active = weekdays.includes(idx);
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => toggleWeekday(idx)}
                        className="inline-flex h-9 w-10 items-center justify-center rounded-xl text-[12px] font-semibold transition-all"
                        style={{
                          background: active
                            ? "var(--color-pitch-lime)"
                            : "rgba(255,255,255,0.04)",
                          color: active ? "#1d3700" : "#f4f5f3",
                          border: active
                            ? "1px solid var(--color-pitch-lime)"
                            : "1px solid rgba(255,255,255,0.08)",
                        }}
                        aria-pressed={active}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </Field>
            )}

            {repeat !== "none" && (
              <p
                className="text-[11px] leading-relaxed"
                style={{ color: "var(--color-on-surface-variant)" }}
              >
                Alle Termine der Serie sind verknüpft. Beim Bearbeiten oder
                Löschen kannst du wählen: nur diesen Termin, diesen und folgende
                oder die gesamte Serie.
              </p>
            )}
          </div>
        )}

        {/* ── Series scope picker (when editing a series row) ─────── */}
        {initial && isSeriesMember && (
          <Field label="Änderung anwenden auf">
            <div className="flex flex-col gap-1.5">
              {(
                [
                  ["single", "Nur diesen Termin"],
                  ["future", "Diesen und folgende"],
                  ["all", "Alle Termine der Serie"],
                ] as const
              ).map(([value, label]) => (
                <label
                  key={value}
                  className="flex items-center gap-2 rounded-xl px-3 py-2 text-[12.5px]"
                  style={{
                    background:
                      scope === value
                        ? "rgba(151,252,0,0.06)"
                        : "rgba(255,255,255,0.03)",
                    border:
                      scope === value
                        ? "1px solid rgba(151,252,0,0.35)"
                        : "1px solid rgba(255,255,255,0.06)",
                    color: "#f4f5f3",
                  }}
                >
                  <input
                    type="radio"
                    name="scope"
                    value={value}
                    checked={scope === value}
                    onChange={() => setScope(value)}
                    className="accent-(--color-pitch-lime)"
                  />
                  {label}
                </label>
              ))}
            </div>
          </Field>
        )}

        <Field label="Notizen (optional)">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Fokus, Übungen, Hinweise…"
            className="min-h-20 w-full rounded-xl px-3 py-2 text-[13px] outline-none"
            style={inputStyle}
          />
        </Field>

        {error && (
          <p
            className="rounded-xl border px-3 py-2 text-[12px]"
            style={{
              borderColor: "rgba(255,107,107,0.30)",
              background: "rgba(255,107,107,0.08)",
              color: "var(--color-error, #ff6b6b)",
            }}
          >
            {error}
          </p>
        )}

        <footer className="mt-1 flex items-center justify-between gap-3">
          {initial ? (
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[12px] font-semibold transition-colors disabled:opacity-60"
              style={{
                color: "#ff8a8a",
                border: "1px solid rgba(255,138,138,0.25)",
              }}
            >
              <Trash2 className="size-3.5" />
              {isSeriesMember && scope === "all"
                ? "Serie löschen"
                : isSeriesMember && scope === "future"
                  ? "Folgende löschen"
                  : "Löschen"}
            </button>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-xl px-3 py-2 text-[12px] transition-colors"
              style={{
                border: "1px solid rgba(255,255,255,0.10)",
                color: "var(--color-on-surface-variant)",
              }}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded-xl px-4 py-2 text-[13px] font-semibold disabled:opacity-60"
              style={{
                background: "var(--color-pitch-lime)",
                color: "#1d3700",
                boxShadow: "0 6px 16px -4px rgba(151,252,0,0.45)",
              }}
            >
              {busy
                ? "Speichere…"
                : initial
                  ? isSeriesMember && scope === "all"
                    ? "Serie speichern"
                    : isSeriesMember && scope === "future"
                      ? "Folgende speichern"
                      : "Speichern"
                  : repeat === "none"
                    ? "Anlegen"
                    : repeat === "weekly"
                      ? `${Math.max(1, Math.min(repeatCount, 52)) * Math.max(1, weekdays.length)} Termine anlegen`
                      : `${Math.max(1, Math.min(repeatCount, 52))} Termine anlegen`}
            </button>
          </div>
        </footer>
      </form>
      {confirmDialog}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "#f4f5f3",
  colorScheme: "dark",
};

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span
        className="text-[10px] font-semibold uppercase tracking-[0.18em]"
        style={{ color: "var(--color-on-surface-variant)" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

/* ─────────────────────────── date helpers ─────────────────────────── */

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
/** Monday-based start of week. */
function startOfWeek(d: Date): Date {
  const x = startOfDay(d);
  const dow = x.getDay(); // 0=Sun..6=Sat
  const diff = (dow + 6) % 7; // Mon=0
  x.setDate(x.getDate() - diff);
  return x;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function isoDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
/**
 * ISO-8601 week number (Mo-based, week containing the year's first Thursday
 * is week 1). Used for the "KW" column in the month grid — matches the
 * convention German users see in Outlook, Google Calendar, etc.
 */
function isoWeek(d: Date): number {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (x.getUTCDay() + 6) % 7; // Mo=0
  x.setUTCDate(x.getUTCDate() - dayNum + 3); // nearest Thursday
  const firstThu = new Date(Date.UTC(x.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - firstDayNum + 3);
  return (
    1 +
    Math.round((x.getTime() - firstThu.getTime()) / (7 * 24 * 60 * 60 * 1000))
  );
}
/** Monday-based weekday index from a `YYYY-MM-DD` string (0=Mo..6=So). */
function mondayWeekday(isoDate: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return 0;
  const d = new Date(Number(m[1]!), Number(m[2]!) - 1, Number(m[3]!));
  const dow = d.getDay(); // 0=Sun..6=Sat
  return (dow + 6) % 7; // 0=Mon..6=Sun
}
function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}
function formatTime(iso: string): string {
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
/** "2025-05-26" + "18:00" → ISO string in local TZ. */
function combineDateTime(date: string, time: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!m || !t) return null;
  const d = new Date(
    Number(m[1]!),
    Number(m[2]!) - 1,
    Number(m[3]!),
    Number(t[1]!),
    Number(t[2]!),
    0,
    0,
  );
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
